/**
 * Route table. Pages are thin: they compose features/entities.
 * Public pages live under <RootLayout />; the app under <RequireAuth /> + <AppLayout />.
 */
import type { RouteObject } from 'react-router';

import {
  AccountPage,
  SecurityPage,
  TemplatesPage,
  ShortcutsPage,
  AppHomePage,
  BoardPage,
  CategoryPage,
  CardRoute,
  DatabasePage,
  DocumentPage,
  FeedbackPage,
  HelpPage,
  HomePage,
  InterfaceSettingsPage,
  InvitationsPage,
  JoinPage,
  LoginPage,
  GlobalCalendarPage,
  MyTasksPage,
  NotFoundPage,
  NewSuggestionPage,
  NotificationsPage,
  RegisterPage,
  RouteErrorPage,
  SuggestionPage,
  WorkspaceCalendarPage,
  WorkspaceCardRoute,
  WorkspacePage,
  WorkspaceTrashPage,
} from '@/pages';

import { GuestOnly, RequireAuth } from './guards/RequireAuth';
import { AppLayout } from './layouts/AppLayout';
import { RootLayout } from './layouts/RootLayout';

export const routes: RouteObject[] = [
  {
    errorElement: <RouteErrorPage />,
    children: [
      // The landing page has its own header and footer (features/landing), not the shared shell.
      { index: true, element: <HomePage /> },
      {
        element: <RootLayout />,
        children: [
          { path: 'join', element: <JoinPage /> },
          {
            element: <GuestOnly />,
            children: [
              { path: 'login', element: <LoginPage /> },
              { path: 'register', element: <RegisterPage /> },
            ],
          },
        ],
      },
      {
        path: 'app',
        element: <RequireAuth />,
        children: [
          {
            element: <AppLayout />,
            children: [
              { index: true, element: <AppHomePage /> },
              { path: 'browse/:category', element: <CategoryPage /> },
              { path: 'help', element: <HelpPage /> },
              { path: 'feedback', element: <FeedbackPage /> },
              { path: 'feedback/new', element: <NewSuggestionPage /> },
              { path: 'feedback/:suggestionId', element: <SuggestionPage /> },
              { path: 'my-tasks', element: <MyTasksPage /> },
              { path: 'calendar', element: <GlobalCalendarPage /> },
              { path: 'invitations', element: <InvitationsPage /> },
              { path: 'notifications', element: <NotificationsPage /> },
              { path: 'workspace-trash', element: <WorkspaceTrashPage /> },
              { path: 'workspaces/:workspaceId', element: <WorkspacePage /> },
              {
                path: 'workspaces/:workspaceId/calendar',
                element: <WorkspaceCalendarPage />,
                children: [{ path: 'cards/:cardId', element: <WorkspaceCardRoute /> }],
              },
              {
                path: 'boards/:boardId',
                element: <BoardPage />,
                children: [{ path: 'cards/:cardId', element: <CardRoute /> }],
              },
              { path: 'databases/:databaseId/:tableId?', element: <DatabasePage /> },
              { path: 'documents/:documentId', element: <DocumentPage /> },
              { path: 'account', element: <AccountPage /> },
              { path: 'account/security', element: <SecurityPage /> },
              { path: 'account/templates', element: <TemplatesPage /> },
              { path: 'settings/interface', element: <InterfaceSettingsPage /> },
              { path: 'settings/shortcuts', element: <ShortcutsPage /> },
            ],
          },
        ],
      },
      { element: <RootLayout />, children: [{ path: '*', element: <NotFoundPage /> }] },
    ],
  },
];
